import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Grid,
  Link,
  Paper,
} from '@mui/material';
import {
  Close as CloseIcon,
  OpenInNew as ExternalLinkIcon,
  Science as TrialIcon,
  Medication as DrugIcon,
  Event as EventIcon,
  AttachMoney as FinancialIcon,
  AutoAwesome as AIIcon,
  Person as InsiderIcon,
  ShoppingCart as BuyIcon,
  Sell as SellIcon,
} from '@mui/icons-material';
import { companyApi } from '../../services/api';

// Helper to format dates
const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

// Helper to format currency
const formatCurrency = (value) => {
  if (value === null || value === undefined) return 'N/A';
  if (Math.abs(value) >= 1000) return `$${(value / 1000).toFixed(1)}B`;
  return `$${value.toFixed(0)}M`;
};

// Helper to format insider transaction values
const formatValue = (value) => {
  if (value === null || value === undefined) return 'N/A';
  if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value.toLocaleString()}`;
};

// Helper to format share counts
const formatShares = (value) => {
  if (value === null || value === undefined) return 'N/A';
  if (value >= 1000000) return `${(value / 1000000).toFixed(2)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
  return value.toLocaleString();
};

// Record type configurations
const recordTypeConfig = {
  event: {
    icon: <EventIcon />,
    color: '#2196f3',
    title: 'Event Details',
  },
  pipeline: {
    icon: <DrugIcon />,
    color: '#4caf50',
    title: 'Drug Pipeline Details',
  },
  trial: {
    icon: <TrialIcon />,
    color: '#9c27b0',
    title: 'Clinical Trial Details',
  },
  financial: {
    icon: <FinancialIcon />,
    color: '#ff9800',
    title: 'Financial Details',
  },
  insider: {
    icon: <InsiderIcon />,
    color: '#673ab7',
    title: 'Insider Transaction Details',
  },
};

function RecordDetailModal({ open, onClose, recordType, record, companyId, companyName }) {
  const [aiSummary, setAiSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [summaryError, setSummaryError] = useState(null);

  const config = recordTypeConfig[recordType] || recordTypeConfig.event;

  // Generate AI summary when modal opens
  useEffect(() => {
    if (open && record && companyId) {
      generateSummary();
    }
    return () => {
      setAiSummary(null);
      setSummaryError(null);
    };
  }, [open, record, companyId]);

  const generateSummary = async () => {
    setLoadingSummary(true);
    setSummaryError(null);

    try {
      let question = '';

      switch (recordType) {
        case 'trial':
          question = `Give me a brief summary of the clinical trial ${record.nct_number} "${record.brief_title}". Include the phase, status, primary endpoints, and any notable information about this trial.`;
          break;
        case 'pipeline':
          question = `Summarize the drug "${record.drug_name}" in ${companyName}'s pipeline. Include its current phase, indication, and any recent developments or trial results.`;
          break;
        case 'event':
          question = `Summarize this ${record.event_type?.replace('_', ' ')} event for ${companyName}. What are the key details and implications? Event summary: ${record.summary || 'N/A'}`;
          break;
        case 'financial':
          question = `Analyze ${companyName}'s financial position for ${record.period_type} ${record.fiscal_year}. Key metrics: Revenue ${formatCurrency(record.total_revenue)}, R&D ${formatCurrency(record.r_and_d_expense)}, Net Income ${formatCurrency(record.net_income)}, Cash ${formatCurrency(record.total_cash)}.`;
          break;
        case 'insider':
          question = `Tell me about this insider transaction at ${companyName}. ${record.insider_name} (${record.insider_title || 'insider'}) made a ${record.transaction_type} of ${formatShares(record.shares_transacted)} shares worth ${formatValue(record.total_value)} on ${formatDate(record.transaction_date)}. What does this insider activity suggest? Are there any related insider activities for this company?`;
          break;
        default:
          question = `Tell me more about this record for ${companyName}.`;
      }

      const response = await companyApi.askAboutCompany(companyId, { question, top_k: 3 });
      setAiSummary(response.data.answer);
    } catch (err) {
      console.error('Failed to generate summary:', err);
      setSummaryError('Could not generate AI summary.');
    } finally {
      setLoadingSummary(false);
    }
  };

  const renderEventDetails = () => (
    <Grid container spacing={2}>
      <Grid item xs={6}>
        <Typography variant="caption" color="text.secondary">Event Type</Typography>
        <Typography variant="body2">{record.event_type?.replace('_', ' ') || 'N/A'}</Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="caption" color="text.secondary">Date</Typography>
        <Typography variant="body2">{formatDate(record.event_date)}</Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="caption" color="text.secondary">Drug</Typography>
        <Typography variant="body2">{record.drug_name || 'N/A'}</Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="caption" color="text.secondary">Sentiment</Typography>
        <Chip
          label={record.sentiment || 'Neutral'}
          size="small"
          color={record.sentiment === 'POSITIVE' ? 'success' : record.sentiment === 'NEGATIVE' ? 'error' : 'default'}
        />
      </Grid>
      {record.summary && (
        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary">Summary</Typography>
          <Typography variant="body2">{record.summary}</Typography>
        </Grid>
      )}
      {record.filing_url && (
        <Grid item xs={12}>
          <Link href={record.filing_url} target="_blank" rel="noopener" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            View SEC Filing <ExternalLinkIcon fontSize="small" />
          </Link>
        </Grid>
      )}
    </Grid>
  );

  const renderPipelineDetails = () => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h6">{record.drug_name}</Typography>
        {record.drug_name_generic && (
          <Typography variant="body2" color="text.secondary">{record.drug_name_generic}</Typography>
        )}
      </Grid>
      <Grid item xs={6}>
        <Typography variant="caption" color="text.secondary">Phase</Typography>
        <Box>
          <Chip label={record.phase?.replace('_', ' ') || 'Unknown'} size="small" color="primary" />
        </Box>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="caption" color="text.secondary">Status</Typography>
        <Box>
          <Chip label={record.status || 'Unknown'} size="small" variant="outlined" />
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="caption" color="text.secondary">Indication</Typography>
        <Typography variant="body2">{record.indication || record.indication_broad || 'N/A'}</Typography>
      </Grid>
      {record.pdufa_date && (
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">PDUFA Date</Typography>
          <Typography variant="body2" fontWeight="medium" color="primary">
            {formatDate(record.pdufa_date)}
          </Typography>
        </Grid>
      )}
      {record.nct_numbers && record.nct_numbers.length > 0 && (
        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary">Related Trials</Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
            {record.nct_numbers.slice(0, 5).map((nct) => (
              <Chip
                key={nct}
                label={nct}
                size="small"
                variant="outlined"
                onClick={() => window.open(`https://clinicaltrials.gov/study/${nct}`, '_blank')}
                sx={{ cursor: 'pointer' }}
              />
            ))}
            {record.nct_numbers.length > 5 && (
              <Chip label={`+${record.nct_numbers.length - 5} more`} size="small" />
            )}
          </Box>
        </Grid>
      )}
    </Grid>
  );

  const renderTrialDetails = () => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Chip
            label={record.nct_number}
            color="primary"
            onClick={() => window.open(`https://clinicaltrials.gov/study/${record.nct_number}`, '_blank')}
            sx={{ cursor: 'pointer' }}
          />
          <Chip label={record.phase || 'Phase Unknown'} size="small" variant="outlined" />
          <Chip
            label={record.overall_status}
            size="small"
            color={record.overall_status === 'COMPLETED' ? 'success' : record.overall_status === 'RECRUITING' ? 'primary' : 'default'}
            variant="outlined"
          />
        </Box>
      </Grid>
      <Grid item xs={12}>
        <Typography variant="caption" color="text.secondary">Title</Typography>
        <Typography variant="body2">{record.brief_title}</Typography>
      </Grid>
      {record.official_title && record.official_title !== record.brief_title && (
        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary">Official Title</Typography>
          <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{record.official_title}</Typography>
        </Grid>
      )}
      <Grid item xs={6}>
        <Typography variant="caption" color="text.secondary">Enrollment</Typography>
        <Typography variant="body2">{record.enrollment_count || 'N/A'} participants</Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="caption" color="text.secondary">Start Date</Typography>
        <Typography variant="body2">{formatDate(record.start_date)}</Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="caption" color="text.secondary">Primary Completion</Typography>
        <Typography variant="body2">{formatDate(record.primary_completion_date)}</Typography>
      </Grid>
      <Grid item xs={6}>
        <Typography variant="caption" color="text.secondary">Completion</Typography>
        <Typography variant="body2">{formatDate(record.completion_date)}</Typography>
      </Grid>
      {record.conditions && record.conditions.length > 0 && (
        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary">Conditions</Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
            {record.conditions.map((cond, i) => (
              <Chip key={i} label={cond} size="small" variant="outlined" />
            ))}
          </Box>
        </Grid>
      )}
      <Grid item xs={12}>
        <Link
          href={`https://clinicaltrials.gov/study/${record.nct_number}`}
          target="_blank"
          rel="noopener"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
        >
          View on ClinicalTrials.gov <ExternalLinkIcon fontSize="small" />
        </Link>
      </Grid>
    </Grid>
  );

  const renderFinancialDetails = () => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h6">
          {record.period_type} {record.fiscal_year}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Period ending {formatDate(record.period_end)}
        </Typography>
      </Grid>
      <Grid item xs={6} sm={4}>
        <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">Revenue</Typography>
          <Typography variant="h6">{formatCurrency(record.total_revenue)}</Typography>
        </Paper>
      </Grid>
      <Grid item xs={6} sm={4}>
        <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">R&D Expense</Typography>
          <Typography variant="h6">{formatCurrency(record.r_and_d_expense)}</Typography>
        </Paper>
      </Grid>
      <Grid item xs={6} sm={4}>
        <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">Net Income</Typography>
          <Typography variant="h6" color={record.net_income < 0 ? 'error.main' : 'success.main'}>
            {formatCurrency(record.net_income)}
          </Typography>
        </Paper>
      </Grid>
      <Grid item xs={6} sm={4}>
        <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">Cash & Equivalents</Typography>
          <Typography variant="h6">{formatCurrency(record.total_cash)}</Typography>
        </Paper>
      </Grid>
      <Grid item xs={6} sm={4}>
        <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">Total Assets</Typography>
          <Typography variant="h6">{formatCurrency(record.total_assets)}</Typography>
        </Paper>
      </Grid>
      {record.runway_months && (
        <Grid item xs={6} sm={4}>
          <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">Cash Runway</Typography>
            <Typography
              variant="h6"
              color={record.runway_months < 12 ? 'error.main' : record.runway_months < 24 ? 'warning.main' : 'success.main'}
            >
              {Math.round(record.runway_months)} mo
            </Typography>
          </Paper>
        </Grid>
      )}
    </Grid>
  );

  const renderInsiderDetails = () => {
    const isBuy = record.transaction_type === 'BUY';
    const isSell = record.transaction_type === 'SELL';
    const TypeIcon = isBuy ? BuyIcon : isSell ? SellIcon : InsiderIcon;
    const typeColor = isBuy ? '#4caf50' : isSell ? '#f44336' : '#757575';

    // Build role string
    const roles = [];
    if (record.is_officer) roles.push('Officer');
    if (record.is_director) roles.push('Director');
    if (record.is_ten_percent_owner) roles.push('10% Owner');
    const roleStr = roles.length > 0 ? roles.join(', ') : 'Insider';

    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <TypeIcon sx={{ color: typeColor, fontSize: 28 }} />
            <Box>
              <Typography variant="h6" sx={{ color: typeColor }}>
                {record.transaction_type} - {formatValue(record.total_value)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatShares(record.shares_transacted)} shares @ ${record.price_per_share?.toFixed(2) || 'N/A'}
              </Typography>
            </Box>
          </Box>
        </Grid>

        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">Insider Name</Typography>
          <Typography variant="body1" fontWeight="medium">{record.insider_name || 'N/A'}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">Title/Role</Typography>
          <Typography variant="body1">{record.insider_title || 'N/A'}</Typography>
          <Typography variant="caption" color="text.secondary">{roleStr}</Typography>
        </Grid>

        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">Transaction Date</Typography>
          <Typography variant="body2">{formatDate(record.transaction_date)}</Typography>
        </Grid>
        <Grid item xs={6}>
          <Typography variant="caption" color="text.secondary">Filing Date</Typography>
          <Typography variant="body2">{formatDate(record.filing_date)}</Typography>
        </Grid>

        <Grid item xs={6} sm={4}>
          <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">Shares Transacted</Typography>
            <Typography variant="h6">{formatShares(record.shares_transacted)}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4}>
          <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center' }}>
            <Typography variant="caption" color="text.secondary">Price Per Share</Typography>
            <Typography variant="h6">${record.price_per_share?.toFixed(2) || 'N/A'}</Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={4}>
          <Paper variant="outlined" sx={{ p: 1.5, textAlign: 'center', bgcolor: isBuy ? '#e8f5e9' : isSell ? '#ffebee' : 'inherit' }}>
            <Typography variant="caption" color="text.secondary">Total Value</Typography>
            <Typography variant="h6" sx={{ color: typeColor }}>{formatValue(record.total_value)}</Typography>
          </Paper>
        </Grid>

        {record.shares_owned_after && (
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">Shares Owned After Transaction</Typography>
            <Typography variant="body2">{formatShares(record.shares_owned_after)} shares</Typography>
          </Grid>
        )}

        {record.sec_filing_url && (
          <Grid item xs={12}>
            <Link href={record.sec_filing_url} target="_blank" rel="noopener" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              View SEC Form 4 Filing <ExternalLinkIcon fontSize="small" />
            </Link>
          </Grid>
        )}
      </Grid>
    );
  };

  const renderDetails = () => {
    switch (recordType) {
      case 'event':
        return renderEventDetails();
      case 'pipeline':
        return renderPipelineDetails();
      case 'trial':
        return renderTrialDetails();
      case 'financial':
        return renderFinancialDetails();
      case 'insider':
        return renderInsiderDetails();
      default:
        return <Typography>No details available.</Typography>;
    }
  };

  if (!record) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
        <Box sx={{ color: config.color }}>{config.icon}</Box>
        <Typography variant="h6">{config.title}</Typography>
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Record Details */}
        <Box sx={{ mb: 3 }}>
          {renderDetails()}
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* AI Summary */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <AIIcon color="primary" fontSize="small" />
            <Typography variant="subtitle2" fontWeight="medium">
              AI Summary
            </Typography>
          </Box>

          {loadingSummary ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Generating summary...
              </Typography>
            </Box>
          ) : summaryError ? (
            <Typography variant="body2" color="error">
              {summaryError}
            </Typography>
          ) : aiSummary ? (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: 'grey.50',
                borderLeft: '3px solid',
                borderColor: 'primary.main',
              }}
            >
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {aiSummary}
              </Typography>
            </Paper>
          ) : null}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default RecordDetailModal;
